/**
 * 重なりと全体の工程表の吹き出し（#102、docs/39 §5.2 の8、§5.3.1・§5.3.5、Figma C3）
 *   ConflictPop … 案件の工程表の「重なり」：「10/14・10/15 はほかの現場にも入っています」・相手（現場・工程・日付）・「工程表を開く」・「10/17(土) から始める」
 *   Pop        … 押した所の近くに出す吹き出し（画面に対して置く。外を押す・Esc・スクロールで閉じる）
 */
import { mdOf, mdwOf, scheduleHref, type ScheduleConflict } from "@genbaryoku/contract";
import { SCHEDULE_MSG } from "./msg.js";
import { t } from "../../labels.js";
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router";

/** 開いている吹き出し（開いた順）。中から開いた吹き出しを外側が「外」と見ないため・Esc はいちばん上だけ閉じるため */
const openPops: HTMLDivElement[] = [];

/** 押した所の近くの吹き出し（下に入らなければ上に） */
export function Pop({ anchor, onClose, label, testId, children, wide }: { anchor: HTMLElement; onClose: () => void; label: string; testId: string; children: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  useLayoutEffect(() => {
    const r = anchor.getBoundingClientRect();
    const w = ref.current?.offsetWidth ?? 320;
    const h = ref.current?.offsetHeight ?? 200;
    const below = r.bottom + 6 + h <= window.innerHeight - 8;
    setPos({
      top: below ? r.bottom + 6 : Math.max(8, r.top - 6 - h),
      left: Math.max(8, Math.min(r.left + r.width / 2 - w / 2, window.innerWidth - w - 8)),
    });
  }, [anchor]);
  // 閉じる関数は ref で持つ（onClose は呼ぶ側が描くたびに新しくなる）。前は描くたびに受け手を付け直し、行を選んだまま Esc を押すと
  //   工程表の画面が描き直す途中で付け直された受け手がその Esc を受け取らず、1回目の Esc で閉じなかった。中の最初のボタンへ移すのも開いたときだけ
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const el = ref.current;
    if (el) openPops.push(el);
    el?.querySelector<HTMLElement>("button, a")?.focus();
    // 中から開いた吹き出し（行の「報告」の吹き出しの「見送り」など）は body に別に描くので、中に数える。
    //   前は外を押したと見て外側が閉じ、中の見送りの窓ごと消えて見送れなかった（#294 の続き）
    const inside = (t: Node) => (el?.contains(t) ?? false) || openPops.slice(openPops.indexOf(el!) + 1).some((p) => p.contains(t));
    const onDoc = (e: MouseEvent) => {
      if (!inside(e.target as Node) && !anchor.contains(e.target as Node)) closeRef.current();
    };
    const onKey = (e: KeyboardEvent) => {
      // Esc はいちばん上の吹き出しだけ閉じる（中の見送りの窓の Esc で外側まで閉じない）
      if (e.key === "Escape" && openPops[openPops.length - 1] === el) {
        closeRef.current();
        anchor.focus?.();
      }
    };
    const onScroll = (e: Event) => {
      if (inside(e.target as Node)) return;
      closeRef.current();
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      const i = el ? openPops.indexOf(el) : -1;
      if (i >= 0) openPops.splice(i, 1);
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [anchor]);
  return createPortal(
    <div ref={ref} className={"sch-pop" + (wide ? " wide" : "")} role="dialog" aria-label={label} style={pos ?? { top: -9999, left: -9999 }} data-testid={testId}>
      {children}
    </div>,
    document.body,
  );
}

/** 案件の工程表の重なりの吹き出し（から始める は直せるときだけ） */
export function ConflictPop({
  conflict,
  startDate,
  anchor,
  readOnly,
  onClose,
  onStart,
}: {
  conflict: ScheduleConflict;
  startDate: string | null;
  anchor: HTMLElement;
  readOnly: boolean;
  onClose: () => void;
  onStart: (date: string) => void;
}) {
  const body = SCHEDULE_MSG.conflictBody(conflict.days.map(mdOf).join("・"));
  return (
    <Pop anchor={anchor} onClose={onClose} label={body} testId="sch-conflict-pop" wide>
      <p className="sch-poph shu">
        <svg className="cri" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 3l10 18H2zM12 10v4M12 17.5v.5" />
        </svg>
        {body}
      </p>
      <ul className="sch-poplist">
        {conflict.others.map((o) => (
          <li key={o.rowId}>
            <span className="t">
              <b>{o.groupName}</b>
              <small>
                {[o.rowName || "（名前なし）", o.start === o.end ? mdOf(o.start) : `${mdOf(o.start)}〜${mdOf(o.end)}`].join("・")}
              </small>
            </span>
            <Link className="btn" to={scheduleHref(o, o.rowId)} data-testid="sch-conflict-open">
              {SCHEDULE_MSG.openSchedule}
            </Link>
          </li>
        ))}
      </ul>
      {!readOnly && startDate ? (
        <button type="button" className="btn primary" onClick={() => onStart(startDate)} data-testid="sch-conflict-start">
          {SCHEDULE_MSG.startFrom(mdwOf(startDate))}
        </button>
      ) : null}
    </Pop>
  );
}
